import { Avatar, Badge, Caption1, Tooltip, makeStyles, mergeClasses, shorthands, tokens } from "@fluentui/react-components";
import {
  CalendarLtr12Regular,
  CheckmarkCircle12Regular,
  Diamond12Regular,
  FolderRegular,
  Important12Filled,
  LinkMultipleRegular,
  QuestionCircle12Regular,
  TaskListSquareLtrRegular,
} from "@fluentui/react-icons";
import { useMemo, type ReactElement, type ReactNode } from "react";
import { CellLabel, type CellTone } from "@/components/data-table";
import type { ExecutionPriority, ExecutionStatus, ExecutionTask, LabelColor, TaskLabel } from "@/engine/types";
import { todayISO } from "@/engine/date";
import { formatDate, formatDateRange } from "@/lib/date-format";
import { checklistProgress, isOverdue, PRIORITY_LABEL, PRIORITY_ORDER, STATUS_LABEL, STATUS_ORDER, undecided } from "@/store/execution-task-ops";
import { PersonAvatar, useDeletedNames } from "@/features/access/PersonAvatar";
import { personFace } from "@/features/access/person-face";
import { useProjectStore } from "@/store/project-store";
import { SCOPE_KIND_LABEL, scopeRefs, type ScopeKind, type ScopeRef, type TaskQueryContext } from "./task-query";
import { priorityGlyph, statusGlyph, type GlyphSize } from "./task-glyphs";

/* ---------------------------------------------------------------- context hook */

/** Query context for the project currently open in the editor. */
export function useTaskContext(): TaskQueryContext {
  const project = useProjectStore((s) => s.project);
  const schedule = useProjectStore((s) => s.derived.schedule);
  return useMemo(() => ({ project, schedule, today: todayISO() }), [project, schedule]);
}

/* ---------------------------------------------------------------- label colors */

export const LABEL_STYLE: Record<LabelColor, { bg: string; fg: string; name: string }> = {
  pink: { bg: tokens.colorPalettePinkBackground2, fg: tokens.colorPalettePinkForeground2, name: "핑크" },
  red: { bg: tokens.colorPaletteRedBackground2, fg: tokens.colorPaletteRedForeground2, name: "빨강" },
  orange: { bg: tokens.colorPaletteDarkOrangeBackground2, fg: tokens.colorPaletteDarkOrangeForeground2, name: "주황" },
  yellow: { bg: tokens.colorPaletteYellowBackground2, fg: tokens.colorPaletteYellowForeground2, name: "노랑" },
  green: { bg: tokens.colorPaletteGreenBackground2, fg: tokens.colorPaletteGreenForeground2, name: "초록" },
  teal: { bg: tokens.colorPaletteTealBackground2, fg: tokens.colorPaletteTealForeground2, name: "청록" },
  blue: { bg: tokens.colorPaletteBlueBackground2, fg: tokens.colorPaletteBlueForeground2, name: "파랑" },
  purple: { bg: tokens.colorPalettePurpleBackground2, fg: tokens.colorPalettePurpleForeground2, name: "보라" },
  gray: { bg: tokens.colorNeutralBackground5, fg: tokens.colorNeutralForeground2, name: "회색" },
};

export const LABEL_DOT_COLOR: Record<LabelColor, string> = {
  pink: tokens.colorPalettePinkBorderActive,
  red: tokens.colorPaletteRedBorderActive,
  orange: tokens.colorPaletteDarkOrangeBorderActive,
  yellow: tokens.colorPaletteYellowBorderActive,
  green: tokens.colorPaletteGreenBorderActive,
  teal: tokens.colorPaletteTealBorderActive,
  blue: tokens.colorPaletteBlueBorderActive,
  purple: tokens.colorPalettePurpleBorderActive,
  gray: tokens.colorNeutralStroke1,
};

const useStyles = makeStyles({
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },
  chipRowNowrap: {
    flexWrap: "nowrap",
    overflow: "hidden",
  },
  avatars: {
    display: "flex",
    alignItems: "center",
  },
  avatar: {
    marginLeft: "-6px",
    boxShadow: `0 0 0 1.5px ${tokens.colorNeutralBackground1}`,
    ":first-child": { marginLeft: 0 },
  },
  scope: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    color: tokens.colorNeutralForeground2,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    cursor: "pointer",
    fontSize: tokens.fontSizeBase200,
    textDecoration: "none",
    ":hover": {
      color: tokens.colorBrandForeground1,
      textDecoration: "underline",
    },
  },
  scopeIcon: {
    display: "inline-flex",
    alignItems: "center",
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  scopeText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  dateText: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    fontVariantNumeric: "tabular-nums",
  },
  tableDate: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontVariantNumeric: "tabular-nums",
  },
  tableDateMuted: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  tableDateOverdue: { color: tokens.colorPaletteRedForeground1 },
  tableDateToday: { color: tokens.colorPaletteDarkOrangeForeground1 },
  dateOverdue: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    fontVariantNumeric: "tabular-nums",
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
  },
  dateToday: {
    color: tokens.colorPaletteDarkOrangeForeground1,
    fontSize: tokens.fontSizeBase200,
    fontVariantNumeric: "tabular-nums",
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
  },
  priorityRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: tokens.fontSizeBase200,
    lineHeight: "16px",
  },
  priorityUrgent: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightRegular,
    gap: "1px",
  },
  priorityUrgentIcon: {
    display: "block",
    marginInline: "-1px",
  },
  priorityImportant: {
    color: tokens.colorPaletteDarkOrangeForeground1,
    fontWeight: tokens.fontWeightRegular,
  },
  priorityNormal: {
    color: tokens.colorNeutralForeground2,
  },
  priorityLow: {
    color: tokens.colorNeutralForeground3,
  },
  checklistRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
  },
  checklistDone: {
    color: tokens.colorPaletteGreenForeground1,
    fontWeight: tokens.fontWeightRegular,
  },
  labelDotRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    maxWidth: "140px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  labelDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  labelDotText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  dismissBtn: {
    cursor: "pointer",
    marginLeft: "2px",
    color: tokens.colorNeutralForeground3,
    ":hover": { color: tokens.colorNeutralForeground1 },
  },
  valueGlyph: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    minWidth: 0,
    maxWidth: "100%",
  },
  glyph: {
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
  },
  undecided: {
    color: tokens.colorNeutralForeground3,
  },
  emptyBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalL}`,
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
    ...shorthands.border("1px", "dashed", tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusLarge,
  },
});

/* ---------------------------------------------------------------- status / priority */

export function statusIcon(status: ExecutionStatus | undefined) {
  return statusGlyph(status, 12);
}

export function StatusBadge({ status, size = "small" }: { status: ExecutionStatus | undefined; size?: "tiny" | "extra-small" | "small" | "medium" }) {
  // "informative" is Fluent's neutral gray; "subtle" is white-on-dark and vanishes on the page background
  const color = status === "done" ? "success" : status === "inProgress" ? "brand" : status === "blocked" ? "danger" : "informative";
  return (
    <Badge appearance={status ? "tint" : "outline"} color={color} size={size} icon={statusIcon(status)}>
      {status ? STATUS_LABEL[status] : "미정"}
    </Badge>
  );
}

export function PriorityBadge({
  priority,
  showUnset = false,
}: {
  priority: ExecutionPriority | undefined;
  size?: "tiny" | "extra-small" | "small" | "medium";
  showUnset?: boolean;
}) {
  const styles = useStyles();
  if (!priority) {
    return showUnset ? <Caption1 style={{ color: tokens.colorNeutralForeground4 }}>—</Caption1> : null;
  }
  if (priority === "urgent") {
    return (
      <span className={mergeClasses(styles.priorityRow, styles.priorityUrgent)}>
        <Important12Filled className={styles.priorityUrgentIcon} />
        <span>{PRIORITY_LABEL.urgent}</span>
      </span>
    );
  }
  if (priority === "important") {
    return (
      <span className={mergeClasses(styles.priorityRow, styles.priorityImportant)}>
        <span>{PRIORITY_LABEL.important}</span>
      </span>
    );
  }
  if (priority === "medium") {
    return (
      <span className={mergeClasses(styles.priorityRow, styles.priorityNormal)}>
        <span>{PRIORITY_LABEL.medium}</span>
      </span>
    );
  }
  return (
    <span className={mergeClasses(styles.priorityRow, styles.priorityLow)}>
      <span>{PRIORITY_LABEL.low}</span>
    </span>
  );
}

/* ---------------------------------------------------------------- table chips */
// 표 셀은 12px 표식과 본문 글자. 색은 표식에만 싣고, 완료·미정·낮음 글자만 옅게 둔다.

function taskStatusTone(status: ExecutionStatus): CellTone {
  return status === "done" ? "muted" : "default";
}

export function TaskStatusChip({ status, size = 12 }: { status: ExecutionStatus | undefined; size?: GlyphSize }) {
  const styles = useStyles();
  return (
    <span className={styles.valueGlyph}>
      <span className={styles.glyph} aria-hidden>
        {statusGlyph(status, size, true)}
      </span>
      <CellLabel tone={status ? taskStatusTone(status) : "muted"}>{status ? STATUS_LABEL[status] : "미정"}</CellLabel>
    </span>
  );
}

function taskPriorityTone(priority: ExecutionPriority): CellTone {
  return priority === "low" ? "muted" : "default";
}

export function TaskPriorityChip({ priority, size = 12 }: { priority: ExecutionPriority | undefined; size?: GlyphSize }) {
  const styles = useStyles();
  if (!priority) return <CellLabel tone="muted">—</CellLabel>;
  return (
    <span className={styles.valueGlyph}>
      <span className={styles.glyph} aria-hidden>
        {priorityGlyph(priority, size)}
      </span>
      <CellLabel tone={taskPriorityTone(priority)}>{PRIORITY_LABEL[priority]}</CellLabel>
    </span>
  );
}

export interface ValueOption {
  value: string;
  label: string;
  icon: ReactElement;
}

/** 서랍·셀렉트가 값을 고를 때. 16px 표식과 이름. */
export const STATUS_VALUE_OPTIONS: ValueOption[] = [
  { value: "", label: "미정", icon: statusGlyph(undefined, 16, true) },
  ...STATUS_ORDER.map((status) => ({ value: status, label: STATUS_LABEL[status], icon: statusGlyph(status, 16, true) })),
];

export const PRIORITY_VALUE_OPTIONS: ValueOption[] = [
  { value: "", label: "미정", icon: priorityGlyph(undefined) },
  ...PRIORITY_ORDER.map((priority) => ({ value: priority, label: PRIORITY_LABEL[priority], icon: priorityGlyph(priority) })),
];

/* ---------------------------------------------------------------- labels */

export function LabelChip({
  label,
  size = "small",
  onClick,
  dismissible,
  variant = "badge",
}: {
  label: TaskLabel;
  size?: "tiny" | "extra-small" | "small" | "medium";
  onClick?: () => void;
  dismissible?: boolean;
  variant?: "dot" | "badge";
}) {
  const styles = useStyles();
  if (variant === "dot") {
    const dotColor = LABEL_DOT_COLOR[label.color] ?? tokens.colorNeutralStroke1;
    return (
      <span
        className={styles.labelDotRow}
        onClick={onClick}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        style={{ cursor: onClick ? "pointer" : undefined }}
        title={label.name}
      >
        <span className={styles.labelDot} style={{ backgroundColor: dotColor }} />
        <span className={styles.labelDotText}>{label.name}</span>
        {dismissible && (
          <span className={styles.dismissBtn} aria-label={`${label.name} 라벨 제거`}>
            ×
          </span>
        )}
      </span>
    );
  }

  const c = LABEL_STYLE[label.color] ?? LABEL_STYLE.gray;
  return (
    <Badge
      appearance="filled"
      size={size}
      shape="rounded"
      style={{ backgroundColor: c.bg, color: c.fg, cursor: onClick ? "pointer" : undefined }}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      aria-label={dismissible ? `${label.name} 라벨 제거` : label.name}
    >
      {label.name}
      {dismissible ? " ×" : ""}
    </Badge>
  );
}

export function LabelChips({
  labelIds,
  labels,
  size = "small",
  max,
  nowrap,
  variant = "dot",
}: {
  labelIds: string[];
  labels: TaskLabel[];
  size?: "tiny" | "extra-small" | "small" | "medium";
  max?: number;
  nowrap?: boolean;
  variant?: "dot" | "badge";
}) {
  const styles = useStyles();
  const items = labels.filter((l) => labelIds.includes(l.id));
  if (!items.length) return null;
  const shown = max != null ? items.slice(0, max) : items;
  const rest = items.slice(shown.length);
  return (
    <span className={mergeClasses(styles.chipRow, nowrap && styles.chipRowNowrap)}>
      {shown.map((l) => (
        <LabelChip key={l.id} label={l} size={size} variant={variant} />
      ))}
      {rest.length > 0 && (
        <Tooltip content={rest.map((l) => l.name).join(", ")} relationship="label">
          <Caption1 style={{ color: tokens.colorNeutralForeground4, fontVariantNumeric: "tabular-nums" }}>
            +{rest.length}
          </Caption1>
        </Tooltip>
      )}
    </span>
  );
}

/* ---------------------------------------------------------------- assignees */

export function AssigneeAvatars({ names, size = 20, max = 3 }: { names: string[]; size?: 16 | 20 | 24 | 28; max?: number }) {
  const styles = useStyles();
  const deletedNames = useDeletedNames();
  if (!names.length) {
    return (
      <Tooltip content="담당 미정" relationship="label">
        <Avatar size={size} color="neutral" aria-label="담당 미정" />
      </Tooltip>
    );
  }
  const shown = names.slice(0, max);
  const rest = names.length - shown.length;
  const labelOf = (name: string) => personFace(name, deletedNames.has(name)).label;
  return (
    <span className={styles.avatars} aria-label={`담당 ${names.map(labelOf).join(", ")}`}>
      {shown.map((n) => (
        <PersonAvatar key={n} name={n} size={size} className={styles.avatar} />
      ))}
      {rest > 0 && <Avatar size={size} initials={`+${rest}`} color="neutral" className={styles.avatar} aria-label={`외 ${rest}명`} />}
    </span>
  );
}

/* ---------------------------------------------------------------- dates */

export function DueChip({ task, today = todayISO() }: { task: ExecutionTask; today?: string; size?: "tiny" | "extra-small" | "small" | "medium" }) {
  const styles = useStyles();
  if (!task.due) return null;
  const overdue = isOverdue(task, today);
  const isToday = task.due === today && task.status !== "done";
  const text = formatDate(task.due, "short");

  if (overdue) {
    return (
      <span className={styles.dateOverdue} title="지연">
        <CalendarLtr12Regular />
        {text}
      </span>
    );
  }
  if (isToday) {
    return (
      <span className={styles.dateToday} title="오늘 마감">
        <CalendarLtr12Regular />
        {text}
      </span>
    );
  }
  return (
    <span className={styles.dateText} style={{ display: "inline-flex", alignItems: "center", gap: "3px" }}>
      <CalendarLtr12Regular style={{ color: tokens.colorNeutralForeground3 }} />
      {text}
    </span>
  );
}

// Table date cells follow the records register: body-size text with year, overdue in red, empty muted.

export function StartDateChip({ task }: { task: ExecutionTask }) {
  const styles = useStyles();
  if (!task.start) return <span className={styles.tableDateMuted}>—</span>;
  return <span className={styles.tableDate}>{formatDate(task.start, "numeric")}</span>;
}

export function DueDateChip({ task, today = todayISO() }: { task: ExecutionTask; today?: string }) {
  const styles = useStyles();
  if (!task.due) return <span className={styles.tableDateMuted}>—</span>;
  const text = formatDate(task.due, "numeric");
  if (isOverdue(task, today)) {
    return (
      <span className={mergeClasses(styles.tableDate, styles.tableDateOverdue)} title="기한 지남">
        {text}
      </span>
    );
  }
  if (task.due === today && task.status !== "done") {
    return (
      <span className={mergeClasses(styles.tableDate, styles.tableDateToday)} title="오늘 마감">
        {text}
      </span>
    );
  }
  return <span className={styles.tableDate}>{text}</span>;
}

/* ---------------------------------------------------------------- scope (WBS/Activity) */

export function scopeIcon(kind: ScopeKind) {
  return kind === "wbs" ? <FolderRegular /> : kind === "milestone" ? <Diamond12Regular /> : kind === "missing" ? <QuestionCircle12Regular /> : <TaskListSquareLtrRegular />;
}

export function ScopeChip({ ref, onOpen }: { ref: ScopeRef; onOpen?: (id: string) => void; size?: "tiny" | "extra-small" | "small" | "medium" }) {
  const styles = useStyles();
  const Icon = scopeIcon(ref.kind);
  return (
    <Tooltip content={`${SCOPE_KIND_LABEL[ref.kind]}${ref.start ? ` · ${formatDateRange(ref.start, ref.finish, "short")}` : ""} · 일정에서 보기`} relationship="description">
      <span
        className={styles.scope}
        role={onOpen ? "button" : undefined}
        tabIndex={onOpen ? 0 : undefined}
        onClick={(e) => {
          e.stopPropagation();
          onOpen?.(ref.id);
        }}
        onKeyDown={(e) => {
          if (onOpen && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            e.stopPropagation();
            onOpen(ref.id);
          }
        }}
      >
        <span className={styles.scopeIcon}>{Icon}</span>
        <span className={styles.scopeText}>
          {ref.wbs ? `${ref.wbs} ` : ""}
          {ref.name}
        </span>
      </span>
    </Tooltip>
  );
}

export function ScopeChips({ task, ctx, onOpen, max = 3, nowrap }: { task: ExecutionTask; ctx: TaskQueryContext; onOpen?: (id: string) => void; max?: number; size?: "tiny" | "extra-small" | "small" | "medium"; nowrap?: boolean }) {
  const styles = useStyles();
  const refs = scopeRefs(task, ctx);
  if (!refs.length) return null;
  const shown = refs.slice(0, max);
  const rest = refs.slice(shown.length);
  return (
    <span className={mergeClasses(styles.chipRow, nowrap && styles.chipRowNowrap)}>
      {shown.map((r) => (
        <ScopeChip key={r.id} ref={r} onOpen={onOpen} />
      ))}
      {rest.length > 0 && (
        <Tooltip content={rest.map((r) => `${r.wbs ? `${r.wbs} ` : ""}${r.name}`).join(", ")} relationship="label">
          <Caption1 style={{ color: tokens.colorNeutralForeground4, fontVariantNumeric: "tabular-nums" }}>
            +{rest.length}
          </Caption1>
        </Tooltip>
      )}
    </span>
  );
}

/* ---------------------------------------------------------------- misc */

export function ChecklistMini({ task }: { task: ExecutionTask }) {
  const styles = useStyles();
  const { done, total } = checklistProgress(task);
  if (!total) return null;
  const isDone = done === total;
  return (
    <Tooltip content={`체크리스트 ${done}개 완료 · 전체 ${total}개`} relationship="label">
      <span
        className={mergeClasses(styles.checklistRow, isDone && styles.checklistDone)}
        aria-label={`체크리스트 ${done}/${total}`}
      >
        {isDone ? (
          <CheckmarkCircle12Regular style={{ color: tokens.colorPaletteGreenForeground1 }} />
        ) : (
          <TaskListSquareLtrRegular style={{ color: tokens.colorNeutralForeground3 }} />
        )}
        <span>{done}/{total}</span>
      </span>
    </Tooltip>
  );
}

const UNDECIDED_LABEL = { status: "상태", assignee: "담당", date: "일정", scope: "범위" } as const;

export function UndecidedHint({ task, className }: { task: ExecutionTask; className?: string }) {
  const styles = useStyles();
  const items = undecided(task);
  if (!items.length) return null;
  return <Caption1 className={mergeClasses(styles.undecided, className)}>{items.map((i) => UNDECIDED_LABEL[i]).join("·")} 미정</Caption1>;
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  const styles = useStyles();
  return (
    <div className={styles.emptyBox}>
      <Caption1 style={{ color: tokens.colorNeutralForeground2 }}>{title}</Caption1>
      {hint && <Caption1>{hint}</Caption1>}
      {action}
    </div>
  );
}
